import React from 'react'
import {
  FormCheckboxField,
  FormField,
  FormMultiSelectField,
  FormReadonlyField,
  FormSelectField,
  FormTextareaField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { CountryComplianceDto } from 'api/Clients/Types/CountryComplianceDto'
import { NameDto } from 'api/Common/Types'
import { TagType } from 'api/Documents/Types/ClientDocument'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { FormSubmitValues } from 'utils/formValidation'

export interface AddEditCountryRiskFormValues {
  id: string
  country: string
  entity: string
  score: number
  manualScoring: boolean
  comment: string
  countryComplianceMrloAnalyses: LabeledValue[]
  internalRiskLevelId: number | string
}

export type AddEditCountryRiskValues = Omit<
  AddEditCountryRiskFormValues,
  'countryComplianceMrloAnalyses'
> & {
  countryComplianceMrloAnalyses: number[]
}

type LabeledValue = {
  id: number | string
  value: number | string
  label: string
}

const EditCountryRiskFormUI: React.FC<FormikProps<AddEditCountryRiskFormValues> & OuterProps> = (
  props
) => {
  const { handleSubmit, setSubmitting, isSubmitting, values, errors, setFieldValue } = props

  const handleSubmitForm = () => {
    setSubmitting(true)
    handleSubmit()
  }

  return (
    <Dialog open={props.isOpen} onOpenChange={(open) => !open && props.onClose()}>
      <DialogContent className='max-w-3xl'>
        <DialogHeader>
          <DialogTitle>Edit Country Risk</DialogTitle>
        </DialogHeader>
        <Form>
          <div className='grid gap-4 md:grid-cols-2'>
            <FormReadonlyField name='country' label='Country' value={values.country} />
            <FormReadonlyField name='entity' label='Entity' value={values.entity} />
            <FormField name='score' label='Score' type='number' error={errors.score} />
            <FormCheckboxField name='manualScoring' label='Manual Scoring' />
            <div className='md:col-span-2'>
              <FormTextareaField name='comment' label='Comment' error={errors.comment} />
            </div>
            <FormSelectField
              name='internalRiskLevelId'
              label='Internal Risk Level'
              options={props.internalRiskLevel || []}
              error={errors.internalRiskLevelId}
              onChange={(value) => setFieldValue('internalRiskLevelId', value ?? '')}
            />
            <FormMultiSelectField
              name='countryComplianceMrloAnalyses'
              label='MRLO Analyses'
              options={props.mrloAnalyses || []}
              value={values.countryComplianceMrloAnalyses.map((analysis) => analysis.value)}
              allowClear
              onChange={(nextValue) => {
                const selectedValues = Array.isArray(nextValue)
                  ? nextValue
                  : nextValue == null
                    ? []
                    : [nextValue]

                setFieldValue(
                  'countryComplianceMrloAnalyses',
                  selectedValues.map((selectedValue) => {
                    const match = (props.mrloAnalyses || []).find(
                      (analysis) => analysis.id === selectedValue
                    )

                    return {
                      id: selectedValue,
                      value: selectedValue,
                      label: match?.name ?? String(selectedValue),
                    }
                  })
                )
              }}
            />
          </div>
        </Form>
        <DialogFooter>
          <Button disabled={isSubmitting} onClick={props.onClose} type='button' variant='outline'>
            Cancel
          </Button>
          <Button disabled={isSubmitting} onClick={handleSubmitForm} type='button'>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function dataToSubmit(
  values: AddEditCountryRiskFormValues
): FormSubmitValues<AddEditCountryRiskValues> {
  return {
    ...values,
    countryComplianceMrloAnalyses: values.countryComplianceMrloAnalyses.map(
      (x) => x.value as number
    ),
  }
}

interface OuterProps {
  isOpen?: boolean
  data: CountryComplianceDto
  mrloAnalyses: NameDto[]
  internalRiskLevel: NameDto[]
  onClose(): void
  onSubmit(values: FormSubmitValues<AddEditCountryRiskValues>): Promise<void>
}

export const EditCountryRiskForm = withFormik<OuterProps, AddEditCountryRiskFormValues>({
  mapPropsToValues: ({ data }) => {
    return {
      id: data.id,
      country: data.country.name,
      entity: data.tickmillCompany.name,
      score: data.score,
      comment: data.comment,
      manualScoring: data.manualScoring,
      countryComplianceMrloAnalyses: (data.countryComplianceMrloAnalyses || []).map(
        (tag: TagType) => ({
          label: tag.name,
          value: tag.id,
          id: tag.id,
        })
      ),
      internalRiskLevelId: data.internalRiskLevel?.id || '',
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<AddEditCountryRiskFormValues> = {}
    if (!values.internalRiskLevelId) {
      errors.internalRiskLevelId = 'required'
    }
    return errors
  },
  enableReinitialize: true,
})(EditCountryRiskFormUI)
